import {
    type ChangeEvent,
    type FocusEvent,
    useState,
    useRef,
    type FC
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    getNativeSelectWebOptionStyle,
    NATIVE_SELECT_WEB_ICON_STYLE,
    getNativeSelectWebStyle
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../../../fieldControl/type";
import type INativeSelectProps from "../../type";
import {
    ChevronDown as ChevronDownIcon
} from "lucide-react-native";
import {
    getNativeSelectIsFocusVisible,
    getNativeSelectSections
} from "../../stylesheet";
import {
    getFieldSizeKey,
    getFieldSize
} from "../../../fieldControl/stylesheet";
import {
    uuid
} from "../../../../utils";
import FieldControl from "../../../fieldControl";

const ChevronIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <ChevronDownIcon
    color={customColor}
    size={size}
/>;

const NativeSelectWeb: FC<INativeSelectProps> = ({
    spreadBehaviour = "baseline",
    isMinWidthEnabled = true,
    onFocus: onFocusProp,
    accessibilityLabel,
    isDisabled = false,
    isRequired = false,
    onBlur: onBlurProp,
    type = "neutral",
    value: valueProp,
    customLocalize,
    autoComplete,
    initialValue,
    customTheme,
    placeholder,
    hintText,
    onChange,
    style,
    title,
    data,
    size,
    id
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const generatedID = useRef(uuid());

    const [
        internalValue,
        setInternalValue
    ] = useState(initialValue ?? "");

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const currentValue = valueProp ?? internalValue;

    const selectedValue = data.some((item) => item.value === currentValue) ? currentValue : "";

    const currentSize = getFieldSizeKey({
        isActionable: true,
        pointerType,
        size
    });

    const currentMeasures = getFieldSize({
        size: currentSize,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const sections = getNativeSelectSections({
        data
    });

    const optionStyle = getNativeSelectWebOptionStyle({
        colors
    });

    const onSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value;

        setInternalValue(nextValue);

        if(onChange) {
            onChange(nextValue);
        }
    };

    const onSelectFocus = (event: FocusEvent<HTMLSelectElement>) => {
        setIsFocused(getNativeSelectIsFocusVisible({
            element: event.currentTarget
        }));

        if(onFocusProp) {
            onFocusProp();
        }
    };

    const onSelectBlur = () => {
        setIsFocused(false);

        if(onBlurProp) {
            onBlurProp();
        }
    };

    const getEndAction = (): FieldActionType => {
        return {
            customIconColor: colors.content.icon.mid,
            style: NATIVE_SELECT_WEB_ICON_STYLE,
            icon: ChevronIcon
        };
    };

    const renderOptions = () => {
        return sections.map((section) => {
            const options = section.items.map((item) => <option
                style={optionStyle}
                value={item.value}
                key={item.value}
            >
                {item.title}
            </option>);

            if(!section.group) {
                return options;
            }

            return <optgroup
                label={section.group}
                style={optionStyle}
                key={section.group}
            >
                {options}
            </optgroup>;
        });
    };

    const renderInput = ({
        placeholderTextColor,
        accessibilityProps,
        style: inputStyle
    }: FieldInputRenderPropsType) => {
        const {
            id: selectID,
            disabled,
            ...ariaProps
        } = accessibilityProps;

        return <select
            {...ariaProps}
            style={getNativeSelectWebStyle({
                valueColor: StyleSheet.flatten(inputStyle)?.color?.toString(),
                placeholderColor: placeholderTextColor,
                isPlaceholder: selectedValue === "",
                currentSize: currentMeasures,
                isDisabled,
                typography,
                colors,
                spaces
            })}
            autoComplete={autoComplete}
            onChange={onSelectChange}
            onFocus={onSelectFocus}
            value={selectedValue}
            onBlur={onSelectBlur}
            required={isRequired}
            disabled={disabled}
            id={selectID}
        >
            <option
                hidden={placeholder === ""}
                style={optionStyle}
                disabled={true}
                value=""
            >
                {placeholder ?? localize("select-an-option")}
            </option>
            {renderOptions()}
        </select>;
    };

    return <FieldControl
        accessibilityLabel={accessibilityLabel}
        isMinWidthEnabled={isMinWidthEnabled}
        inputID={id ?? generatedID.current}
        spreadBehaviour={spreadBehaviour}
        customLocalize={customLocalize}
        endAction={getEndAction()}
        renderInput={renderInput}
        customTheme={customTheme}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isFocused={isFocused}
        hintText={hintText}
        size={currentSize}
        title={title}
        style={style}
        type={type}
    />;
};
export default NativeSelectWeb;
